import _ from 'lodash';
import { useIntl } from 'react-intl';
import React, { FC, useEffect, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { addInfoOrganizationToast } from '../../../errors/toast/ToastUtils';
import useOrganizationsList from '../../../../hooks/api/organization/useOrganizationsList';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import { setOrganization } from '../../../../slices/organization';
import classnames from 'classnames';
import AddOrganizationModal from './addOrganizationModal/AddOrganizationModal';
import { getOrganizationId } from '../../../../utilities/LocalStorage';
import { getOrganizationSecurity } from '../../../../services/organization/OrganizationService';
import Avatar from '../../avatar/Avatar';
import { history } from '../../../../history';
import { DefaultPage } from '../Navbar';

interface OrganizationMenuProps {
  setActivePage: (value: ((prevState: string) => string) | string) => void;
}

const OrganizationMenu: FC<OrganizationMenuProps> = ({ setActivePage }) => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const organization = useSelector((state: RootState) => state.organization.organization);
  const organizations = useSelector((state: RootState) => state.organization.organizations);
  const avatar = useSelector((state: RootState) => state.organization.organization?.avatar);
  const id = useSelector((state: RootState) => state.organization.organization?.id) as string;
  const { getOrganizations } = useOrganizationsList();
  const dispatch = useDispatch();
  const intl = useIntl();

  useEffect(() => {
    getOrganizations();
    setOrganizationFromStorage();
  }, []);

  const setOrganizationFromStorage = async () => {
    const id = getOrganizationId() as string;

    if (id) {
      await getOrganizationSecurity(id).then((response) => {
        dispatch(setOrganization(response.data));
      });
    }
  };

  const handleOrganizationChange = async (organizationItem) => {
    const oldOrganization = organization;
    await getOrganizationSecurity(organizationItem.id).then((response) => {
      dispatch(setOrganization(response.data));
    });
    displayOrganizationToast(organizationItem, oldOrganization);
    setActivePage(DefaultPage);
    history.push(DefaultPage);
  };

  const displayOrganizationToast = (organizationItem, oldOrganization) => {
    if (oldOrganization != organizationItem) {
      addInfoOrganizationToast({
        avatar: organizationItem.avatar,
        message: intl.formatMessage({ id: 'organizationMenu.modal.organizationChange' }) + organizationItem.name,
      });
    }
  };

  const organizationsList = () => {
    if (organizations && organizations.length > 0) {
      return _.map(organizations, (item) => showOrganizationItems(item));
    }
  };

  const showOrganizationItems = (item) => {
    return (
      <React.Fragment key={`organization-item-${item.id}`}>
        <Dropdown.Item
          onClick={() => handleOrganizationChange(item)}
          className={classnames({ 'organization-item': true, 'item-active': id == item.id })}
        >
          <Avatar src={item.avatar} width={36} height={36}></Avatar>
          <div className={classnames({ 'organization-item': true, 'organization-active': id == item.id })}>
            <div>
              <span className="item-name">{item.name}</span>
            </div>
            <span className="check-icon-wrapper">
              <span className="mdi mdi-check-circle"></span>
            </span>
          </div>
        </Dropdown.Item>
        <Dropdown.Divider className="dropdown-divider-normal"></Dropdown.Divider>
      </React.Fragment>
    );
  };

  return (
    <React.Fragment>
      <div className="organization-menu">
        <Dropdown className="dropdown" drop="end">
          <Dropdown.Toggle
            variant="link"
            id="dropdown-basic"
            data-toggle="dropdown"
            className="d-flex align-items-center w-100"
          >
            <Avatar src={avatar} width={36} height={36}></Avatar>
            <span className="ms-3 title organization-name">{organization?.name || ''}</span>
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {organizations && organizations.length > 0 && organizationsList()}
            <Dropdown.Item
              className="add-organization-item"
              onClick={() => {
                setShowModal(true);
              }}
              role="button"
            >
              <span className="icon-wrapper">
                <span className="mdi mdi-plus-circle-outline"></span>
              </span>
              <span className="add-organization-name">
                {intl.formatMessage({ id: 'organizationMenu.addOrganization' })}
              </span>
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>
      <AddOrganizationModal
        show={showModal}
        onClose={() => {
          setShowModal(false);
          getOrganizations();
        }}
      ></AddOrganizationModal>
    </React.Fragment>
  );
};

export default OrganizationMenu;
